/**
 * Terminal 82 - Historical Chart Component
 *
 * TradingView-style candlestick chart with:
 * - Log scale Y-axis
 * - Zoom in/out controls
 * - Mouse wheel zoom / Pinch-to-zoom on touch
 * - Pan/scroll via drag or swipe
 * - Tap to select candle on touch devices
 * - Auto-fit visible range
 */

import type { DayData } from '../types';
import { TIER_COLORS, MYTHIC_EVENT_COLORS, getRarityTier } from '../types';
import { formatDisplayDate, formatPrice } from '../card';

// ============================================
// Types
// ============================================

export interface ChartConfig {
  width: number;
  height: number;
  candleWidth: number;
  candleGap: number;
  paddingTop: number;
  paddingBottom: number;
  paddingLeft: number;
  paddingRight: number;
}

export interface ChartCallbacks {
  onCandleClick: (data: DayData, index: number) => void;
  onCandleHover: (data: DayData | null, index: number) => void;
}

const DEFAULT_CONFIG: ChartConfig = {
  width: 800,
  height: 300,
  candleWidth: 8,
  candleGap: 3,
  paddingTop: 20,
  paddingBottom: 40,
  paddingLeft: 50,
  paddingRight: 20,
};

// Compact price format for Y-axis (no comma, shorter)
function formatAxisPrice(price: number): string {
  if (price >= 1000) {
    return price.toFixed(0); // "3456" instead of "3,456.78"
  }
  return price.toFixed(0);
}

// ============================================
// Chart Component
// ============================================

export function createHistoricalChart(
  container: HTMLElement,
  data: DayData[],
  callbacks: ChartCallbacks,
  config: Partial<ChartConfig> = {}
): {
  update: (newData: DayData[]) => void;
  setSelected: (index: number) => void;
  destroy: () => void;
} {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  let selectedIndex = -1;
  let currentData = data;

  // View state - TradingView style
  let viewStart = Math.max(0, data.length - 90); // Start showing last 90 days
  let viewEnd = data.length;
  let useLogScale = true;

  // Crosshair state
  let hoverIndex = -1; // Index of candle being hovered (-1 = none)

  // Create chart container with controls
  container.innerHTML = `
    <div class="history-chart">
      <div class="chart-header">
        <span class="chart-title">ETH/USD HISTORICAL</span>
        <div class="chart-controls">
          <button class="chart-btn" id="btn-zoom-in" title="Zoom In">+</button>
          <button class="chart-btn" id="btn-zoom-out" title="Zoom Out">-</button>
          <button class="chart-btn" id="btn-fit" title="Fit All">FIT</button>
          <button class="chart-btn ${useLogScale ? 'active' : ''}" id="btn-log" title="Log Scale">LOG</button>
        </div>
        <span class="chart-info" id="chart-hover-info">Drag to pan · Wheel to zoom</span>
      </div>
      <div class="chart-canvas-wrapper">
        <canvas id="history-canvas"></canvas>
        <div class="chart-tooltip" id="chart-tooltip"></div>
      </div>
      <div class="chart-x-axis" id="chart-x-axis"></div>
    </div>
  `;

  const canvas = container.querySelector('#history-canvas') as HTMLCanvasElement;
  const ctx = canvas.getContext('2d')!;
  const tooltip = container.querySelector('#chart-tooltip') as HTMLElement;
  const hoverInfo = container.querySelector('#chart-hover-info') as HTMLElement;
  const xAxis = container.querySelector('#chart-x-axis') as HTMLElement;
  const canvasWrapper = container.querySelector('.chart-canvas-wrapper') as HTMLElement;

  // Control buttons
  const btnZoomIn = container.querySelector('#btn-zoom-in') as HTMLButtonElement;
  const btnZoomOut = container.querySelector('#btn-zoom-out') as HTMLButtonElement;
  const btnFit = container.querySelector('#btn-fit') as HTMLButtonElement;
  const btnLog = container.querySelector('#btn-log') as HTMLButtonElement;

  // Dynamic canvas sizing - fit to container
  function updateCanvasSize() {
    const wrapperWidth = canvasWrapper.clientWidth || cfg.width;
    canvas.width = wrapperWidth;
    canvas.height = cfg.height;
  }

  // Get current chart dimensions
  function getChartDimensions() {
    return {
      width: canvas.width - cfg.paddingLeft - cfg.paddingRight,
      height: canvas.height - cfg.paddingTop - cfg.paddingBottom,
    };
  }

  // Initial size
  updateCanvasSize();

  // Get visible data
  function getVisibleData(): DayData[] {
    return currentData.slice(viewStart, viewEnd);
  }

  // Get visible candles count
  function getVisibleCount(): number {
    return viewEnd - viewStart;
  }

  // Draw the chart
  function render() {
    const { width: chartWidth, height: chartHeight } = getChartDimensions();

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const visibleData = getVisibleData();

    if (visibleData.length === 0) {
      ctx.fillStyle = '#888888';
      ctx.font = '14px IBM Plex Mono';
      ctx.textAlign = 'center';
      ctx.fillText('Loading...', canvas.width / 2, canvas.height / 2);
      return;
    }

    // Calculate price range from VISIBLE data only
    const allPrices = visibleData.flatMap((d) => [d.ohlc.high, d.ohlc.low]);
    const minPrice = Math.min(...allPrices);
    const maxPrice = Math.max(...allPrices);

    // Price to Y conversion
    let priceToY: (price: number) => number;

    if (useLogScale) {
      const logMin = Math.log10(minPrice);
      const logMax = Math.log10(maxPrice);
      const logRange = logMax - logMin || 0.1;
      const logPadding = logRange * 0.1;

      priceToY = (price: number) => {
        const logPrice = Math.log10(price);
        const normalized = (logPrice - (logMin - logPadding)) / (logRange + logPadding * 2);
        return cfg.paddingTop + chartHeight * (1 - normalized);
      };
    } else {
      const priceRange = maxPrice - minPrice || 1;
      const pricePadding = priceRange * 0.1;

      priceToY = (price: number) => {
        const normalized = (price - (minPrice - pricePadding)) / (priceRange + pricePadding * 2);
        return cfg.paddingTop + chartHeight * (1 - normalized);
      };
    }

    // Draw grid and Y-axis using current canvas dimensions
    const dynamicCfg = { ...cfg, width: canvas.width };
    drawGrid(ctx, dynamicCfg, minPrice, maxPrice, priceToY, useLogScale);
    drawYAxis(ctx, dynamicCfg, minPrice, maxPrice, useLogScale);

    // Calculate candle width based on visible count
    // Dynamic ratio: less gap when zoomed in (fewer candles visible)
    const totalCandleWidth = chartWidth / visibleData.length;
    const candleRatio = Math.max(0.6, Math.min(0.85, 1 - visibleData.length / 500));
    const candleWidth = Math.max(2, Math.min(25, totalCandleWidth * candleRatio));

    // Draw candles
    for (let i = 0; i < visibleData.length; i++) {
      const d = visibleData[i];
      const dataIndex = viewStart + i;
      const x = cfg.paddingLeft + i * totalCandleWidth + totalCandleWidth / 2;

      const isSelected = dataIndex === selectedIndex;
      const isBullish = d.ohlc.close >= d.ohlc.open;

      // Draw vertical highlight bar for selected candle (behind the candle)
      if (isSelected) {
        const highlightWidth = totalCandleWidth;
        const tier = getRarityTier(d.change.percent);
        let tierColor: string;
        if (tier === 'mythic') {
          tierColor = isBullish ? MYTHIC_EVENT_COLORS.supernova : MYTHIC_EVENT_COLORS.black_swan;
        } else {
          tierColor = TIER_COLORS[tier];
        }
        // Convert hex to rgba with low opacity
        const r = parseInt(tierColor.slice(1, 3), 16);
        const g = parseInt(tierColor.slice(3, 5), 16);
        const b = parseInt(tierColor.slice(5, 7), 16);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.15)`;
        ctx.fillRect(
          x - highlightWidth / 2,
          cfg.paddingTop,
          highlightWidth,
          chartHeight
        );
      }

      drawCandle(ctx, x, d, priceToY, candleWidth, isBullish, isSelected);
    }

    // Draw crosshair if hovering
    if (hoverIndex >= 0 && hoverIndex < visibleData.length) {
      const hoverData = visibleData[hoverIndex];
      const hoverX = cfg.paddingLeft + hoverIndex * totalCandleWidth + totalCandleWidth / 2;
      const hoverPrice = hoverData.ohlc.close;
      const hoverY = priceToY(hoverPrice);
      drawCrosshair(ctx, dynamicCfg, hoverX, hoverY, hoverPrice, chartHeight);
    }

    // Draw X-axis labels
    renderXAxis(xAxis, visibleData, viewStart, dynamicCfg);

    // Update info
    const rangeText = `${visibleData.length} candles`;
    hoverInfo.textContent = selectedIndex >= 0
      ? formatDisplayDate(currentData[selectedIndex].date)
      : `${rangeText} · Drag to pan · Wheel to zoom`;
  }

  // Zoom functions - smoother increments
  function zoomIn() {
    const center = (viewStart + viewEnd) / 2;
    const newRange = Math.max(5, getVisibleCount() * 0.85); // Smoother: 15% zoom
    viewStart = Math.max(0, Math.floor(center - newRange / 2));
    viewEnd = Math.min(currentData.length, Math.ceil(center + newRange / 2));
    render();
  }

  function zoomOut() {
    const center = (viewStart + viewEnd) / 2;
    const newRange = Math.min(currentData.length, getVisibleCount() * 1.18); // Smoother: 18% zoom
    viewStart = Math.max(0, Math.floor(center - newRange / 2));
    viewEnd = Math.min(currentData.length, Math.ceil(center + newRange / 2));
    render();
  }

  function fitAll() {
    viewStart = 0;
    viewEnd = currentData.length;
    render();
  }

  function toggleLogScale() {
    useLogScale = !useLogScale;
    btnLog.classList.toggle('active', useLogScale);
    render();
  }

  // Mouse wheel zoom - smoother
  function handleWheel(e: WheelEvent) {
    e.preventDefault();

    const { width: chartWidth } = getChartDimensions();
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseRatio = Math.max(0, Math.min(1, (mouseX - cfg.paddingLeft) / chartWidth));

    // Smoother zoom factors
    const zoomFactor = e.deltaY < 0 ? 0.92 : 1.08;
    const newRange = Math.max(5, Math.min(currentData.length, getVisibleCount() * zoomFactor));

    const pivot = viewStart + getVisibleCount() * mouseRatio;
    viewStart = Math.max(0, Math.floor(pivot - newRange * mouseRatio));
    viewEnd = Math.min(currentData.length, viewStart + Math.ceil(newRange));

    render();
  }

  // Drag to pan (mouse and touch)
  let isDragging = false;
  let hasDragged = false;
  let dragStartX = 0;
  let dragStartView = 0;
  let dragRange = 0;

  // Touch state for pinch zoom
  let lastPinchDistance = 0;
  let touchStartTime = 0;

  function handleMouseDown(e: MouseEvent) {
    if (e.button !== 0) return; // Only left mouse button
    e.preventDefault();
    isDragging = true;
    hasDragged = false;
    dragStartX = e.clientX;
    dragStartView = viewStart;
    dragRange = viewEnd - viewStart; // Store the exact range at drag start
  }

  function handleDragStart(e: DragEvent) {
    e.preventDefault(); // Prevent native drag behavior
    return false;
  }

  function handleMouseUp() {
    if (isDragging) {
      isDragging = false;
      // Keep hasDragged true briefly to prevent click from firing
      setTimeout(() => {
        hasDragged = false;
      }, 50);
    }
  }

  function handleMouseMove(e: MouseEvent) {
    const { width: chartWidth } = getChartDimensions();
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (isDragging) {
      e.preventDefault();
      const dx = e.clientX - dragStartX;
      // Only count as dragged if moved more than 3 pixels
      if (Math.abs(dx) > 3) {
        hasDragged = true;
      }
      // Use stored dragRange to keep zoom level constant
      const candlesPerPixel = dragRange / chartWidth;
      const deltaCandlesFloat = -dx * candlesPerPixel;
      const newStart = Math.max(0, Math.min(currentData.length - dragRange, dragStartView + deltaCandlesFloat));
      viewStart = Math.floor(newStart);
      viewEnd = viewStart + dragRange;
      render();
      return;
    }

    // Only process hover if mouse is over canvas
    if (x < 0 || x > canvas.width || y < 0 || y > canvas.height) {
      tooltip.style.display = 'none';
      return;
    }

    // Hover logic
    const visibleData = getVisibleData();
    const totalCandleWidth = chartWidth / visibleData.length;
    const candleIndex = Math.floor((x - cfg.paddingLeft) / totalCandleWidth);
    const dataIndex = viewStart + candleIndex;

    // Update crosshair state
    const newHoverIndex = (candleIndex >= 0 && candleIndex < visibleData.length) ? candleIndex : -1;
    if (newHoverIndex !== hoverIndex) {
      hoverIndex = newHoverIndex;
      render();
    }

    if (candleIndex >= 0 && candleIndex < visibleData.length) {
      const d = visibleData[candleIndex];

      hoverInfo.innerHTML = `
        <span class="date">${formatDisplayDate(d.date)}</span>
        <span class="price">$${formatPrice(d.ohlc.close)}</span>
        <span class="change ${d.change.percent >= 0 ? 'positive' : 'negative'}">
          ${d.change.percent >= 0 ? '+' : ''}${d.change.percent.toFixed(2)}%
        </span>
      `;

      tooltip.style.display = 'block';
      // Smart tooltip positioning to avoid cutoff
      const tooltipWidth = 140;
      const tooltipHeight = 60;
      let tooltipX = x + 15;
      let tooltipY = y - 10;

      // Flip to left if too close to right edge
      if (tooltipX + tooltipWidth > canvas.width - 10) {
        tooltipX = x - tooltipWidth - 15;
      }
      // Keep above cursor but don't go above canvas
      if (tooltipY < 10) {
        tooltipY = y + 20;
      }
      // Don't go below canvas
      if (tooltipY + tooltipHeight > canvas.height - 10) {
        tooltipY = canvas.height - tooltipHeight - 10;
      }

      tooltip.style.left = `${tooltipX}px`;
      tooltip.style.top = `${tooltipY}px`;
      const isBull = d.ohlc.close >= d.ohlc.open;
      const directionLabel = isBull ? 'BULLISH' : 'BEARISH';
      const directionColor = isBull ? '#00cc00' : '#cc0000';
      tooltip.innerHTML = `
        <div class="tooltip-date">${formatDisplayDate(d.date)}</div>
        <div class="tooltip-direction" style="color: ${directionColor}; font-weight: bold; font-size: var(--font-xs);">${directionLabel}</div>
        <div class="tooltip-ohlc">
          O: ${formatPrice(d.ohlc.open)} H: ${formatPrice(d.ohlc.high)}<br>
          L: ${formatPrice(d.ohlc.low)} C: ${formatPrice(d.ohlc.close)}
        </div>
      `;

      callbacks.onCandleHover(d, dataIndex);
    } else {
      tooltip.style.display = 'none';
      callbacks.onCandleHover(null, -1);
    }
  }

  function handleMouseLeave() {
    tooltip.style.display = 'none';
    // Clear crosshair
    if (hoverIndex !== -1) {
      hoverIndex = -1;
      render();
    }
    // Don't reset isDragging here - allow dragging outside canvas
    hoverInfo.textContent = selectedIndex >= 0
      ? formatDisplayDate(currentData[selectedIndex].date)
      : `${getVisibleCount()} candles · Drag to pan · Wheel to zoom`;
  }

  function handleClick(e: MouseEvent) {
    if (hasDragged) return;

    const { width: chartWidth } = getChartDimensions();
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;

    const visibleData = getVisibleData();
    const totalCandleWidth = chartWidth / visibleData.length;
    const candleIndex = Math.floor((x - cfg.paddingLeft) / totalCandleWidth);
    const dataIndex = viewStart + candleIndex;

    if (candleIndex >= 0 && candleIndex < visibleData.length) {
      selectedIndex = dataIndex;
      canvas.focus(); // Focus canvas for keyboard navigation
      render();
      callbacks.onCandleClick(currentData[dataIndex], dataIndex);
    }
  }

  // ============================================
  // Touch Event Handlers
  // ============================================

  function getTouchDistance(touches: TouchList): number {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function getTouchCenter(touches: TouchList): { x: number; y: number } {
    if (touches.length < 2) {
      return { x: touches[0].clientX, y: touches[0].clientY };
    }
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2,
    };
  }

  function handleTouchStart(e: TouchEvent) {
    e.preventDefault();
    touchStartTime = Date.now();

    if (e.touches.length === 1) {
      // Single finger - prepare for pan or tap
      isDragging = true;
      hasDragged = false;
      dragStartX = e.touches[0].clientX;
      dragStartView = viewStart;
      dragRange = viewEnd - viewStart;
    } else if (e.touches.length === 2) {
      // Two fingers - prepare for pinch zoom
      isDragging = false;
      lastPinchDistance = getTouchDistance(e.touches);
    }
  }

  function handleTouchMove(e: TouchEvent) {
    e.preventDefault();
    const { width: chartWidth } = getChartDimensions();

    if (e.touches.length === 1 && isDragging) {
      // Single finger pan
      const dx = e.touches[0].clientX - dragStartX;
      if (Math.abs(dx) > 5) {
        hasDragged = true;
      }
      const candlesPerPixel = dragRange / chartWidth;
      const deltaCandlesFloat = -dx * candlesPerPixel;
      const newStart = Math.max(0, Math.min(currentData.length - dragRange, dragStartView + deltaCandlesFloat));
      viewStart = Math.floor(newStart);
      viewEnd = viewStart + dragRange;
      render();
    } else if (e.touches.length === 2) {
      // Pinch zoom
      const currentDistance = getTouchDistance(e.touches);
      if (lastPinchDistance > 0) {
        const rect = canvas.getBoundingClientRect();
        const center = getTouchCenter(e.touches);
        const centerX = center.x - rect.left;
        const centerRatio = Math.max(0, Math.min(1, (centerX - cfg.paddingLeft) / chartWidth));

        // Calculate zoom based on pinch delta
        const scale = currentDistance / lastPinchDistance;
        const zoomFactor = 1 / scale; // Invert: spread = zoom in
        const newRange = Math.max(5, Math.min(currentData.length, getVisibleCount() * zoomFactor));

        const pivot = viewStart + getVisibleCount() * centerRatio;
        viewStart = Math.max(0, Math.floor(pivot - newRange * centerRatio));
        viewEnd = Math.min(currentData.length, viewStart + Math.ceil(newRange));
        render();
      }
      lastPinchDistance = currentDistance;
    }
  }

  function handleTouchEnd(e: TouchEvent) {
    const touchDuration = Date.now() - touchStartTime;
    const wasTap = !hasDragged && touchDuration < 300;

    if (wasTap && e.changedTouches.length === 1) {
      // Handle as tap - select candle
      const { width: chartWidth } = getChartDimensions();
      const rect = canvas.getBoundingClientRect();
      const x = e.changedTouches[0].clientX - rect.left;

      const visibleData = getVisibleData();
      const totalCandleWidth = chartWidth / visibleData.length;
      const candleIndex = Math.floor((x - cfg.paddingLeft) / totalCandleWidth);
      const dataIndex = viewStart + candleIndex;

      if (candleIndex >= 0 && candleIndex < visibleData.length) {
        selectedIndex = dataIndex;
        render();
        callbacks.onCandleClick(currentData[dataIndex], dataIndex);
      }
    }

    // Reset touch state
    isDragging = false;
    hasDragged = false;
    lastPinchDistance = 0;

    // Update info bar
    hoverInfo.textContent = selectedIndex >= 0
      ? formatDisplayDate(currentData[selectedIndex].date)
      : `${getVisibleCount()} candles · Swipe to pan · Pinch to zoom`;
  }

  // Keyboard navigation - move selection with arrows when candle is selected
  function handleKeyDown(e: KeyboardEvent) {
    // Only handle if canvas is focused
    if (document.activeElement !== canvas) return;

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (selectedIndex > 0) {
        selectedIndex--;
        ensureSelectedVisible();
        render();
        callbacks.onCandleClick(currentData[selectedIndex], selectedIndex);
      }
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (selectedIndex < currentData.length - 1) {
        selectedIndex++;
        ensureSelectedVisible();
        render();
        callbacks.onCandleClick(currentData[selectedIndex], selectedIndex);
      }
    } else if (e.key === '+' || e.key === '=') {
      zoomIn();
    } else if (e.key === '-') {
      zoomOut();
    }
  }

  // Ensure selected candle is visible, scroll if needed
  function ensureSelectedVisible() {
    if (selectedIndex < viewStart) {
      const range = getVisibleCount();
      viewStart = selectedIndex;
      viewEnd = Math.min(currentData.length, viewStart + range);
    } else if (selectedIndex >= viewEnd) {
      const range = getVisibleCount();
      viewEnd = selectedIndex + 1;
      viewStart = Math.max(0, viewEnd - range);
    }
  }

  // Resize handler
  function handleResize() {
    updateCanvasSize();
    render();
  }

  // Prevent native drag on canvas
  canvas.draggable = false;
  canvas.style.userSelect = 'none';
  (canvas.style as any).webkitUserSelect = 'none';

  // Make canvas focusable for keyboard navigation
  canvas.tabIndex = 0;
  canvas.style.outline = 'none'; // Remove default focus outline

  // Attach events - Mouse
  canvas.addEventListener('wheel', handleWheel, { passive: false });
  canvas.addEventListener('mousedown', handleMouseDown);
  canvas.addEventListener('dragstart', handleDragStart);
  document.addEventListener('mouseup', handleMouseUp); // Document-level to catch releases outside canvas
  document.addEventListener('mousemove', handleMouseMove); // Document-level for smooth dragging
  canvas.addEventListener('mouseleave', handleMouseLeave);
  canvas.addEventListener('click', handleClick);
  document.addEventListener('keydown', handleKeyDown);
  window.addEventListener('resize', handleResize);

  // Attach events - Touch
  canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
  canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
  canvas.addEventListener('touchend', handleTouchEnd, { passive: false });
  canvas.addEventListener('touchcancel', handleTouchEnd, { passive: false });

  btnZoomIn.addEventListener('click', zoomIn);
  btnZoomOut.addEventListener('click', zoomOut);
  btnFit.addEventListener('click', fitAll);
  btnLog.addEventListener('click', toggleLogScale);

  // Initial render
  render();

  return {
    update: (newData: DayData[]) => {
      currentData = newData;
      viewStart = Math.max(0, newData.length - 90);
      viewEnd = newData.length;
      render();
    },
    setSelected: (index: number) => {
      selectedIndex = index;
      // Ensure selected candle is visible
      if (index < viewStart || index >= viewEnd) {
        const range = getVisibleCount();
        viewStart = Math.max(0, index - Math.floor(range / 2));
        viewEnd = Math.min(currentData.length, viewStart + range);
      }
      render();
    },
    destroy: () => {
      // Remove mouse events
      canvas.removeEventListener('wheel', handleWheel);
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      canvas.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
      // Remove touch events
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('touchcancel', handleTouchEnd);
    },
  };
}

// ============================================
// Drawing Functions
// ============================================

function drawGrid(
  ctx: CanvasRenderingContext2D,
  cfg: ChartConfig,
  minPrice: number,
  maxPrice: number,
  priceToY: (p: number) => number,
  isLog: boolean
) {
  ctx.strokeStyle = 'rgba(200, 200, 200, 0.15)';
  ctx.lineWidth = 1;

  const gridPrices = isLog
    ? generateLogGridPrices(minPrice, maxPrice)
    : generateLinearGridPrices(minPrice, maxPrice);

  for (const price of gridPrices) {
    const y = priceToY(price);
    if (y > cfg.paddingTop && y < cfg.height - cfg.paddingBottom) {
      ctx.beginPath();
      ctx.moveTo(cfg.paddingLeft, y);
      ctx.lineTo(cfg.width - cfg.paddingRight, y);
      ctx.stroke();
    }
  }
}

function generateLogGridPrices(min: number, max: number): number[] {
  const prices: number[] = [];
  const logMin = Math.floor(Math.log10(min));
  const logMax = Math.ceil(Math.log10(max));

  for (let exp = logMin; exp <= logMax; exp++) {
    const base = Math.pow(10, exp);
    [1, 2, 5].forEach((mult) => {
      const price = base * mult;
      if (price >= min * 0.9 && price <= max * 1.1) {
        prices.push(price);
      }
    });
  }

  return prices;
}

function generateLinearGridPrices(min: number, max: number): number[] {
  const range = max - min;
  const step = Math.pow(10, Math.floor(Math.log10(range))) / 2;
  const prices: number[] = [];

  let price = Math.floor(min / step) * step;
  while (price <= max * 1.1) {
    if (price >= min * 0.9) {
      prices.push(price);
    }
    price += step;
  }

  return prices;
}

function drawYAxis(
  ctx: CanvasRenderingContext2D,
  cfg: ChartConfig,
  minPrice: number,
  maxPrice: number,
  isLog: boolean
) {
  ctx.fillStyle = '#888888';
  ctx.font = '10px IBM Plex Mono';
  ctx.textAlign = 'right'; // Right-align so text ENDS at position

  const chartHeight = cfg.height - cfg.paddingTop - cfg.paddingBottom;
  const gridPrices = isLog
    ? generateLogGridPrices(minPrice, maxPrice)
    : generateLinearGridPrices(minPrice, maxPrice);

  let priceToY: (price: number) => number;

  if (isLog) {
    const logMin = Math.log10(minPrice);
    const logMax = Math.log10(maxPrice);
    const logRange = logMax - logMin || 0.1;
    const logPadding = logRange * 0.1;

    priceToY = (price: number) => {
      const logPrice = Math.log10(price);
      const normalized = (logPrice - (logMin - logPadding)) / (logRange + logPadding * 2);
      return cfg.paddingTop + chartHeight * (1 - normalized);
    };
  } else {
    const priceRange = maxPrice - minPrice || 1;
    const pricePadding = priceRange * 0.1;

    priceToY = (price: number) => {
      const normalized = (price - (minPrice - pricePadding)) / (priceRange + pricePadding * 2);
      return cfg.paddingTop + chartHeight * (1 - normalized);
    };
  }

  // Filter labels to prevent overlap (minimum 18px between labels)
  const minLabelSpacing = 18;
  let lastDrawnY = -Infinity;

  // Check if zoomed out (price range > 1000x means full history visible)
  const isFullyZoomedOut = maxPrice / minPrice > 500;

  for (const price of gridPrices) {
    const y = priceToY(price);
    if (y > cfg.paddingTop + 10 && y < cfg.height - cfg.paddingBottom - 10) {
      // Skip if too close to last drawn label
      if (Math.abs(y - lastDrawnY) < minLabelSpacing) {
        continue;
      }
      // Draw on LEFT side - right-aligned, ending before chart area
      ctx.textAlign = 'right';
      ctx.fillText(formatAxisPrice(price), cfg.paddingLeft - 5, y + 4);
      lastDrawnY = y;
    }
  }

  // Draw max price at top when fully zoomed out
  if (isFullyZoomedOut) {
    const maxY = priceToY(maxPrice);
    if (maxY > cfg.paddingTop && Math.abs(maxY - lastDrawnY) >= minLabelSpacing) {
      ctx.textAlign = 'right';
      ctx.fillText(formatAxisPrice(maxPrice), cfg.paddingLeft - 5, maxY + 4);
    }
  }
}

function drawCandle(
  ctx: CanvasRenderingContext2D,
  x: number,
  dayData: DayData,
  priceToY: (p: number) => number,
  width: number,
  isBullish: boolean,
  isSelected: boolean
) {
  const { ohlc } = dayData;
  const highY = priceToY(ohlc.high);
  const lowY = priceToY(ohlc.low);
  const openY = priceToY(ohlc.open);
  const closeY = priceToY(ohlc.close);

  const bodyTop = Math.min(openY, closeY);
  const bodyBottom = Math.max(openY, closeY);
  const bodyHeight = Math.max(1, bodyBottom - bodyTop);

  // Check if this is the Genesis candle (first Terminal 82 mint - Jan 26, 2026)
  const isGenesis = dayData.date === '2026-01-26';

  // Use rarity tier color (mythic uses event colors based on direction)
  // Genesis always uses white
  let color: string;
  if (isGenesis) {
    color = '#FFFFFF';
  } else {
    const tier = getRarityTier(dayData.change.percent);
    if (tier === 'mythic') {
      // Mythic: gold for supernova (bullish), red for black swan (bearish)
      color = isBullish ? MYTHIC_EVENT_COLORS.supernova : MYTHIC_EVENT_COLORS.black_swan;
    } else {
      color = TIER_COLORS[tier];
    }
  }

  // Genesis candle gets persistent glow
  if (isGenesis) {
    ctx.shadowColor = '#FFFFFF';
    ctx.shadowBlur = 8;
  }

  // Draw wicks
  ctx.strokeStyle = color;
  ctx.lineWidth = isSelected ? 2 : 1;

  // Top wick (high to body top)
  ctx.beginPath();
  ctx.moveTo(x, highY);
  ctx.lineTo(x, bodyTop);
  ctx.stroke();

  // Bottom wick (body bottom to low)
  ctx.beginPath();
  ctx.moveTo(x, bodyBottom);
  ctx.lineTo(x, lowY);
  ctx.stroke();

  // Draw body - filled for bearish, hollow for bullish
  if (isSelected && !isGenesis) {
    ctx.shadowColor = color;
    ctx.shadowBlur = 10;
  }

  ctx.beginPath();
  ctx.rect(x - width / 2, bodyTop, width, bodyHeight);

  if (isBullish) {
    // Hollow body for bullish (up day)
    ctx.strokeStyle = color;
    ctx.lineWidth = isSelected ? 2 : 1;
    ctx.stroke();
  } else {
    // Filled body for bearish (down day)
    ctx.fillStyle = color;
    ctx.fill();
  }

  ctx.shadowBlur = 0;

  // Genesis marker - small diamond above the candle
  if (isGenesis) {
    const markerY = highY - 8;
    const markerSize = 4;
    
    ctx.fillStyle = '#FFFFFF';
    ctx.shadowColor = '#FFFFFF';
    ctx.shadowBlur = 6;
    
    ctx.beginPath();
    ctx.moveTo(x, markerY - markerSize);
    ctx.lineTo(x + markerSize, markerY);
    ctx.lineTo(x, markerY + markerSize);
    ctx.lineTo(x - markerSize, markerY);
    ctx.closePath();
    ctx.fill();
    
    ctx.shadowBlur = 0;
  }
}

function renderXAxis(
  container: HTMLElement,
  data: DayData[],
  _startIndex: number,
  cfg: ChartConfig
) {
  const chartWidth = cfg.width - cfg.paddingLeft - cfg.paddingRight;
  const labelInterval = Math.max(1, Math.floor(data.length / 8));

  let html = '';
  for (let i = 0; i < data.length; i += labelInterval) {
    const d = data[i];
    const x = cfg.paddingLeft + (i + 0.5) * (chartWidth / data.length);
    const date = new Date(d.date + 'T00:00:00Z'); // Parse as UTC
    const label = `${date.getUTCMonth() + 1}/${date.getUTCDate()}`;
    html += `<span style="left: ${x}px">${label}</span>`;
  }

  container.innerHTML = html;
}

/**
 * Draw crosshair with vertical line and price indicator
 */
function drawCrosshair(
  ctx: CanvasRenderingContext2D,
  cfg: ChartConfig,
  x: number,
  y: number,
  price: number,
  chartHeight: number
) {
  // Vertical line (dashed)
  ctx.strokeStyle = 'rgba(200, 200, 200, 0.4)';
  ctx.lineWidth = 1;
  ctx.setLineDash([3, 3]);
  ctx.beginPath();
  ctx.moveTo(x, cfg.paddingTop);
  ctx.lineTo(x, cfg.paddingTop + chartHeight);
  ctx.stroke();

  // Horizontal line (dashed)
  ctx.beginPath();
  ctx.moveTo(cfg.paddingLeft, y);
  ctx.lineTo(cfg.width - cfg.paddingRight, y);
  ctx.stroke();
  ctx.setLineDash([]);

  // Price label on LEFT side
  const labelText = formatAxisPrice(price);
  ctx.font = 'bold 10px IBM Plex Mono';
  const labelWidth = ctx.measureText(labelText).width + 8;
  const labelHeight = 16;
  const labelX = cfg.paddingLeft - labelWidth - 2;
  const labelY = y - labelHeight / 2;

  // Label background
  ctx.fillStyle = 'rgba(200, 200, 200, 0.9)';
  ctx.fillRect(labelX, labelY, labelWidth, labelHeight);

  // Label text
  ctx.fillStyle = '#000';
  ctx.textAlign = 'left';
  ctx.fillText(labelText, labelX + 4, y + 4);
}
